import { ChartData, ChartTypeRegistry } from 'chart.js';
import {
  AxisChartByNumberPrediction,
  ChartDisplayType,
  DataPointSetByNumberOptimisation,
  DataPointSetByNumberPrediction,
  DataPointSetStyle
} from '../chart.models';
import { useMemo } from 'react';
import { ChartLine } from 'common/components/charts/chart-line';
import { chartColours } from 'common/components/charts/chart-colours';

export const ChartStrategyNumberPrediction = ({
  chart
}: {
  chart: AxisChartByNumberPrediction;
}) => {
  const chartData = useMemo(() => {
    if (chart.dataPointSetWithPredictions.length > 0) {
      return createChartData(
        chart.dataPointSetWithPredictions,
        chart.dataPointSetWithOptimisations
      );
    }
  }, [chart.dataPointSetWithPredictions, chart.dataPointSetWithOptimisations]);

  const tryRenderChart = () => {
    switch (chart.defaultChartDisplayType) {
      case ChartDisplayType.LINE:
        return (
          <ChartLine
            title={chart.title}
            data={chartData as ChartData<'line'>}
            isStackedX={false}
            isStackedY={false}
            isLinearX={true}
            legendKeysToIgnore={getLegendKeysToIgnore(
              chart.dataPointSetWithPredictions
            )}
            xAxisTitle={chart.xAxisTitle}
            yAxisTitle={chart.yAxisTitle}
            key={`${chart.id}-line`}
          />
        );
      default:
        throw Error(
          `You cannot use chart type "${chart.defaultChartDisplayType}" for number charts`
        );
    }
  };

  return chartData ? <>{tryRenderChart()}</> : null;
};

function createChartData(
  dataPointSetWithPredictions: DataPointSetByNumberPrediction[],
  dataPointSetWithOptimisations: DataPointSetByNumberOptimisation[]
) {
  const actualDataPointSetNames = dataPointSetWithPredictions
    .filter((dps) => dps.actualParentDataPointSetName === '')
    .map((dps) => dps.dataPointSetName);

  const actualDataPointSetNamesDistinct = [...new Set(actualDataPointSetNames)];

  const actualChartDataSets = actualDataPointSetNamesDistinct.map(
    (dataPointSetName, dataPointSetNameIndex) =>
      createPredictiveChartDataSet(
        dataPointSetName,
        dataPointSetNameIndex,
        dataPointSetWithPredictions
      )
  );

  const predictionDataPointSetNames = dataPointSetWithPredictions
    .filter((dps) => dps.actualParentDataPointSetName !== '')
    .map((dps) => dps.dataPointSetName);

  const predictionDataPointSetNamesDistinct = [
    ...new Set(predictionDataPointSetNames)
  ];

  const childChartDataSets = predictionDataPointSetNamesDistinct.map(
    (dataPointSetName) => {
      const actualIndex = getActualDataPointSetNameIndex(
        dataPointSetName,
        dataPointSetWithPredictions,
        actualDataPointSetNamesDistinct
      );

      return createPredictiveChartDataSet(
        dataPointSetName,
        actualIndex,
        dataPointSetWithPredictions
      );
    }
  );

  const optimisationDataSet = createOptimisationChartDataSet(
    dataPointSetWithOptimisations,
    actualDataPointSetNamesDistinct
  );

  const chartData: ChartData<keyof ChartTypeRegistry> = {
    datasets: [
      ...actualChartDataSets,
      ...childChartDataSets,
      optimisationDataSet
    ]
  };

  return chartData;
}

function getActualDataPointSetNameIndex(
  dataPointSetName: string,
  dataPointSets: DataPointSetByNumberPrediction[],
  dataPointSetNames: string[]
): number {
  const actualDataPointSetName =
    dataPointSets.find((dps) => dps.dataPointSetName === dataPointSetName)
      ?.actualParentDataPointSetName ?? '';
  return dataPointSetNames.findIndex((dpsn) => dpsn === actualDataPointSetName);
}

function createPredictiveChartDataSet(
  dataPointSetName: string,
  index: number,
  dataPoints: DataPointSetByNumberPrediction[]
) {
  const chartDataSetStyle =
    dataPoints.find((dp) => dp.dataPointSetName === dataPointSetName)?.style ??
    DataPointSetStyle.SOLID;

  const dataSet: any = {
    label: dataPointSetName,
    backgroundColor: chartColours[index],
    borderColor: chartColours[index],
    fill: false,
    borderWidth: chartDataSetStyle === DataPointSetStyle.SOLID ? 2 : 1,
    borderDash: chartDataSetStyle === DataPointSetStyle.SOLID ? [0, 0] : [5, 5],
    pointStyle: false,
    parsing: false,
    order: 2,
    data: dataPoints
      .filter((dp) => dp.dataPointSetName === dataPointSetName)
      .map((dataPoint) => {
        return {
          x: dataPoint.xValue,
          y: dataPoint.yValue
        };
      })
  };
  return dataSet;
}

function createOptimisationChartDataSet(
  dataPoints: DataPointSetByNumberOptimisation[],
  actualDataPointSetNamesDistinct: string[]
) {
  const scatterDataSet: any = {
    label: 'Optimisation',
    fill: false,
    type: 'scatter',
    pointRadius: 6,
    pointHoverRadius: 8,
    order: 1,
    backgroundColor: chartColours[actualDataPointSetNamesDistinct.length + 1],
    data: dataPoints.map((dp) => {
      return {
        x: dp.xValue,
        y: dp.yValue
      };
    })
  };

  return scatterDataSet;
}

function getLegendKeysToIgnore(
  dataPointSets: DataPointSetByNumberPrediction[]
): string[] {
  return dataPointSets
    .filter((dps) => dps.actualParentDataPointSetName !== '')
    .map((dps) => dps.dataPointSetName);
}
